import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { BottomNav } from '../components/BottomNav';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  BookOpen, Heart, HandHeart, Play, Check, 
  Settings, Sun, Send, Share2, Bookmark, ChevronRight,
  MessageCircle, Sparkles, PenLine, Library as LibraryIcon
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Home() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [dailyPlan, setDailyPlan] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [kindnessText, setKindnessText] = useState('');
  const [activeTab, setActiveTab] = useState('today');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [planRes, progressRes] = await Promise.all([
        axios.get(`${API}/daily/plan`),
        axios.get(`${API}/progress`)
      ]);
      setDailyPlan(planRes.data);
      setProgress(progressRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Could not load your daily plan');
    } finally {
      setLoading(false);
    }
  };

  const completedSteps = dailyPlan?.completed_steps || [];
  const allStepsComplete = completedSteps.length >= 4;

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 17) return 'Good Afternoon';
    return 'Good Evening';
  };

  const shareVerse = async () => {
    if (!dailyPlan) return;
    
    const shareText = `"${dailyPlan.verse}"\n— ${dailyPlan.verse_reference}\n\nShared from BlessBe 🙏💙`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Daily Verse from BlessBe',
          text: shareText
        });
        toast.success('Verse shared!');
      } catch (error) {
        if (error.name !== 'AbortError') {
          await navigator.clipboard.writeText(shareText);
          toast.success('Verse copied!');
        }
      }
    } else {
      await navigator.clipboard.writeText(shareText);
      toast.success('Verse copied!');
    }
  };

  const bookmarkVerse = async () => {
    if (!dailyPlan) return;
    
    try {
      await axios.post(`${API}/bookmarks`, {
        type: 'verse',
        content: dailyPlan.verse,
        reference: dailyPlan.verse_reference
      });
      toast.success('Verse saved!');
    } catch (error) {
      toast.error('Could not save verse');
    }
  };

  const sendKindText = () => {
    if (!kindnessText.trim()) {
      toast.error('Write something kind first!');
      return;
    }
    
    const message = `${kindnessText}\n\n— Sent with love via BlessBe 💙`;
    
    if (navigator.share) {
      navigator.share({ text: message }).catch(() => {
        navigator.clipboard.writeText(message);
        toast.success('Message copied! Now send it to someone special.');
      });
    } else {
      navigator.clipboard.writeText(message);
      toast.success('Message copied! Now send it to someone special.');
    }
    setKindnessText('');
  };

  if (loading) {
    return (
      <div className="min-h-screen sky-gradient flex items-center justify-center">
        <div className="animate-gentle-pulse text-center">
          <img src="/images/logo.png" alt="BlessBe" className="w-20 h-20 mx-auto mb-4 rounded-full" />
          <p className="text-gray-600 font-medium">Loading your blessings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen sky-gradient-light pb-safe" data-testid="home-page">
      {/* Header */}
      <header className="px-6 pt-8 pb-4">
        <div className="flex items-center justify-between mb-2">
          <div>
            <p className="text-blue-500 font-semibold text-sm">{getGreeting()},</p>
            <h1 className="text-2xl font-bold text-gray-800">{user?.name || 'Friend'}</h1>
          </div>
          <Link to="/settings" data-testid="settings-link">
            <Button variant="ghost" size="icon" className="rounded-full bg-white/80 shadow-sm hover:bg-white">
              <Settings className="w-5 h-5 text-gray-600" />
            </Button>
          </Link>
        </div>
        
        {/* Tab Navigation */}
        <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
          <Button
            variant={activeTab === 'today' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveTab('today')}
            className={`rounded-full whitespace-nowrap ${activeTab === 'today' ? 'bg-blue-500' : 'bg-white/80'}`}
            data-testid="tab-today"
          >
            <Sun className="w-4 h-4 mr-1.5" />
            Today
          </Button>
          <Button
            variant={activeTab === 'library' ? 'default' : 'outline'}
            size="sm"
            onClick={() => navigate('/library')}
            className={`rounded-full whitespace-nowrap ${activeTab === 'library' ? 'bg-blue-500' : 'bg-white/80'}`}
            data-testid="tab-library"
          >
            <BookOpen className="w-4 h-4 mr-1.5" />
            Library
          </Button>
          <Button
            variant={activeTab === 'journal' ? 'default' : 'outline'}
            size="sm"
            onClick={() => navigate('/journal')}
            className={`rounded-full whitespace-nowrap ${activeTab === 'journal' ? 'bg-blue-500' : 'bg-white/80'}`}
            data-testid="tab-journal"
          >
            <PenLine className="w-4 h-4 mr-1.5" />
            Journal
          </Button>
          <Button
            variant={activeTab === 'bible' ? 'default' : 'outline'}
            size="sm"
            onClick={() => navigate('/bible')}
            className={`rounded-full whitespace-nowrap ${activeTab === 'bible' ? 'bg-blue-500' : 'bg-white/80'}`}
            data-testid="tab-bible"
          >
            <BookOpen className="w-4 h-4 mr-1.5" />
            Read Bible
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-6 space-y-5">
        {/* Daily Motivation */}
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-yellow-500" />
          <p className="text-sm text-gray-600 font-medium">Show kindness to everyone today</p>
        </div>

        {/* Daily Verse Card */}
        {dailyPlan && (
          <Card 
            className="rounded-3xl border-0 shadow-card overflow-hidden animate-slide-up bg-gradient-to-br from-blue-50 via-sky-50 to-white"
            data-testid="daily-verse-card"
          >
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <BookOpen className="w-5 h-5 text-blue-500" />
                <span className="text-sm font-semibold text-blue-500">Today&apos;s Verse</span>
              </div>
              
              <blockquote className="text-lg md:text-xl font-medium text-gray-800 leading-relaxed mb-3">
                &ldquo;{dailyPlan.verse}&rdquo;
              </blockquote>
              <p className="text-sm text-blue-600 font-semibold mb-4">{dailyPlan.verse_reference}</p>
              
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                {dailyPlan.explanation}
              </p>
              
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="rounded-full border-blue-200 text-blue-600 hover:bg-blue-50"
                  onClick={shareVerse}
                  data-testid="share-verse-btn"
                >
                  <Share2 className="w-4 h-4 mr-1" />
                  Share
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="rounded-full border-blue-200 text-blue-600 hover:bg-blue-50"
                  onClick={bookmarkVerse}
                  data-testid="bookmark-verse-btn"
                >
                  <Bookmark className="w-4 h-4 mr-1" />
                  Save
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Start Session / Completed Card */}
        {!allStepsComplete ? (
          <Button
            onClick={() => navigate('/session')}
            className="w-full h-14 rounded-2xl text-lg font-bold bg-blue-500 hover:bg-blue-600 text-white shadow-lg hover:shadow-xl transition-all hover:scale-[1.02]"
            data-testid="start-session-btn"
          >
            <Play className="w-5 h-5 mr-2" />
            Start Today&apos;s Devotional
          </Button>
        ) : (
          <Card className="rounded-2xl bg-green-50 border-green-200 shadow-sm">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center shadow-md">
                <Check className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="font-bold text-green-800">All done for today!</p>
                <p className="text-sm text-green-600">You&apos;re spreading blessings</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* How will you show kindness? */}
        <Card className="rounded-2xl border-0 shadow-card bg-white">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <HandHeart className="w-5 h-5 text-pink-500" />
              <span className="font-bold text-gray-800">How will you show kindness today?</span>
            </div>
            
            <div className="flex gap-2">
              <Input
                value={kindnessText}
                onChange={(e) => setKindnessText(e.target.value)}
                placeholder="Write something kind..."
                className="flex-1 h-11 rounded-xl bg-gray-50 border-gray-200"
                data-testid="kindness-input"
              />
              <Button 
                onClick={sendKindText}
                className="h-11 px-4 rounded-xl bg-pink-500 hover:bg-pink-600 text-white"
                data-testid="send-kind-btn"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Kindness Challenge */}
        {dailyPlan && !completedSteps.includes('challenge') && (
          <Card className="rounded-2xl border-0 shadow-card bg-gradient-to-r from-amber-50 to-yellow-50">
            <CardContent className="p-5">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-400 flex items-center justify-center flex-shrink-0 shadow-md">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-bold text-amber-800 mb-1">Today&apos;s Kindness Challenge</p>
                  <p className="text-sm text-amber-700">{dailyPlan.challenge_text}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-3">
          <Link to="/bible" data-testid="quick-bible">
            <Card className="rounded-2xl border-0 shadow-card hover:shadow-float transition-all cursor-pointer bg-white">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1">
                  <p className="font-bold text-sm text-gray-800">Read Bible</p>
                  <p className="text-xs text-gray-500">Any chapter</p>
                </div>
              </CardContent>
            </Card>
          </Link>
          <Link to="/chat" data-testid="quick-chat">
            <Card className="rounded-2xl border-0 shadow-card hover:shadow-float transition-all cursor-pointer bg-white">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-pink-100 flex items-center justify-center">
                  <MessageCircle className="w-5 h-5 text-pink-500" />
                </div>
                <div className="flex-1">
                  <p className="font-bold text-sm text-gray-800">Pray</p>
                  <p className="text-xs text-gray-500">Start praying</p>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* Progress Preview */}
        {progress && (
          <Card className="rounded-2xl border-0 shadow-card bg-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                    <span className="text-2xl">🙏</span>
                  </div>
                  <div>
                    <p className="font-bold text-gray-800">{progress.streak_count} Day Streak</p>
                    <p className="text-sm text-gray-500">{progress.total_sessions} total sessions</p>
                  </div>
                </div>
                <Link to="/progress">
                  <Button variant="ghost" size="sm" className="rounded-full text-blue-500 hover:bg-blue-50">
                    See Impact
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}
      </main>

      <BottomNav />
    </div>
  );
}
